/**
 * Bridge between the planning-mode draft and the compliance adapter (SE-7002).
 *
 * The four action modals need the SAME verdict, computed over the SAME
 * scenario, so the store read and the mapping into the compliance domain live
 * here once rather than four times. It exists on the squad-planning side of
 * the seam on purpose: `features/financial-compliance` must not learn how
 * Squad Planning stores a draft, or the adapter stops being swappable when the
 * FC backend lands.
 *
 * "Scenario-cumulative" is what this hook is for, for the compliance verdict
 * and the Net book value row. Both describe the whole scenario AFTER the
 * action being edited, so every already-staged draft action is handed to the
 * adapter alongside the terms currently being typed — and the adapter drops
 * this player's staged copy so an edit does not count him twice. The Wage/yr
 * and Amortisation/yr rows are NOT cumulative, by design (SE-8994) — each is
 * this one action's own figure, isolated from every other draft action
 * already staged.
 * @module features/squad-planning/hooks/useLiveImpact
 */

import { useMemo } from "react";

import { useDraftEvaluation } from "@/features/financial-compliance/adapters/draftEvaluationSource";
import { useScenarioImpact } from "@/features/financial-compliance/adapters/scenarioImpactSource";
import { hasSufficientTerms } from "@/features/financial-compliance/domain/actionFinancials";

import {
  buildDraftPayload,
  usePlanningMode,
} from "../stores/planningModeStore";
import { mergeEditedAction } from "../utils/actionTermsToScenarioInput";
import { useSquadPlanningPlayers } from "./useSquadPlanningPlayers";

import type {
  ActionTerms,
  PlayerBaselineFinancials,
} from "@/features/financial-compliance/domain/actionFinancials";
import type { StagedAction } from "@/features/financial-compliance/adapters/scenarioImpactSource";
import type { LiveImpactModel } from "@/features/financial-compliance/types";
import type { ActionDetails } from "../types";

/**
 * Map a stored draft detail onto the compliance domain's action terms.
 *
 * A stored Loan carries the covered share it was confirmed with (SE-7324),
 * and that share feeds `wageSharePercent` verbatim — no complement, no
 * rescaling, and NO substitute when it is absent.
 *
 * A loan persisted before the field existed has no share, and this hook used
 * to hand the compliance domain a 100 in its place, which made the cumulative
 * panel report full wage relief for a deal nobody had entered terms for
 * (SE-7411). Zero is not the fix either — that asserts the borrowing club
 * covers nothing. `null` travels through as `null`, the domain declines to put
 * a wage figure it does not have into the total, and the panel says so out
 * loud. This is the same "never assume a share" rationale
 * `hasSufficientTerms` applies to the terms being typed, now applied to the
 * ones already staged.
 * @param details - The stored action details.
 * @returns The equivalent action terms, or null for an unrecognised action.
 */
export const toActionTerms = (details: ActionDetails): ActionTerms | null => {
  switch (details.action) {
    case "Sign":
      return {
        kind: "Sign",
        transferFee: details.transferFee,
        annualSalary: details.annualSalary,
        durationMonths: details.durationMonths,
        signingBonus: details.signingBonus,
        agentFee: details.agentFee,
      };
    case "Sell":
      return { kind: "Sell", salePrice: details.salePrice };
    case "Loan":
      return {
        kind: "Loan",
        loanFee: details.loanFee,
        durationMonths: details.durationMonths,
        wageSharePercent: details.coveredSharePercent,
      };
    case "Promote":
      return {
        kind: "Promote",
        annualSalary: details.annualSalary,
        durationMonths: details.durationMonths,
      };
    default:
      return null;
  }
};

/**
 * Sum the roster's book values into the club's (SE-8794).
 *
 * Over `players` — the ROSTER, First Team + Academy (SE-8346) — and never
 * `rawTargets`: a watchlist target is not on the club's books until he is
 * signed, and his signing reaches the row through its own Sign contribution.
 *
 * A player with no figure counts as zero rather than voiding the whole book,
 * matching how his own baseline reads it. But a roster where NO player carries
 * one is a payload without book values, not a club that owns nothing, so that
 * is null — and the panel prints a dash rather than "€ 0.0M".
 * @param players - The roster rows.
 * @returns The summed book value, or null when no player carries one.
 */
export const sumRosterNetBookValue = (
  players: readonly { readonly netBookValue?: number | null }[]
): number | null => {
  const known = players.flatMap(player =>
    player.netBookValue != null && Number.isFinite(player.netBookValue)
      ? [player.netBookValue]
      : []
  );
  return known.length === 0
    ? null
    : known.reduce((total, value) => total + value, 0);
};

/**
 * Read the live, scenario-cumulative impact of the terms currently on screen.
 * @param terms - The in-flight terms from the open modal.
 * @param playerId - The player being acted on, when the caller knows it.
 * @returns The panel model and the scenario the "View full impact" link opens.
 */
export const useLiveImpact = (
  terms: ActionTerms,
  playerId?: string | null
): {
  readonly model: LiveImpactModel;
  readonly scenarioId: string | null;
} => {
  const { draftActions, draftDetails, draftSignedPlayers, editingScenarioId } =
    usePlanningMode();
  const { players, rawTargets = [] } = useSquadPlanningPlayers();

  const stagedActions = useMemo<readonly StagedAction[]>(
    () =>
      Object.entries(draftDetails).flatMap(([id, details]) => {
        const staged = toActionTerms(details);
        return staged ? [{ playerId: id, terms: staged }] : [];
      }),
    [draftDetails]
  );

  // Without this, `useScenarioImpact` falls back to EMPTY_PLAYER_BASELINE for
  // every player — the whole reason Wage/yr (and, for a Sell, Amortisation/yr
  // and Net book value) read €0.0M regardless of the acted-on player's real
  // figures or the covered share entered (SE-7635). Built once here, from the
  // SAME squad-players read `KpiAreaContainer`'s own `getPlayerWage` already
  // uses, rather than threaded in by each of the four modal containers —
  // `useLiveImpact` is the one place all four already share this verdict, per
  // this file's own header.
  //
  // `amortisationAnnual`/`netBookValue` default to 0 rather than being left
  // undefined: both are `SquadPlayer` fields absent until backend SE-7410
  // populates them, and a Loan's own contribution never reads either field
  // (the registration stays with the parent club), so this only matters for
  // Sell today and degrades the same way an unpopulated column already does
  // elsewhere on this player.
  // SE-8346: rawTargets covers a signed WATCHLIST target the same way
  // `players` used to (before it stopped including the Targets tier — see
  // SquadPlanningPlayersContext's module doc). Read straight off the raw
  // fields (wage/amortisationAnnual/netBookValue exist on the raw row
  // already, same names) rather than paying to format all ~427 into full
  // SquadPlayer shape just for this lookup. Without this, a Sign staged
  // against a target not on the roster would silently fall back to
  // EMPTY_PLAYER_BASELINE — the exact SE-7635 bug this baseline exists to
  // prevent.
  const playerBaselines = useMemo<
    Readonly<Record<string, PlayerBaselineFinancials>>
  >(
    () =>
      Object.fromEntries([
        ...players.map(player => [
          player.id,
          {
            wagePerYear: player.wage ?? 0,
            amortisationPerYear: player.amortisationAnnual ?? 0,
            netBookValue: player.netBookValue ?? 0,
          },
        ]),
        ...rawTargets.map(target => [
          target.id,
          {
            wagePerYear: target.wage ?? 0,
            amortisationPerYear: target.amortisationAnnual ?? 0,
            netBookValue: target.netBookValue ?? 0,
          },
        ]),
      ]),
    [players, rawTargets]
  );

  // What the SERVER is asked to evaluate: every staged action, with the terms
  // currently being typed spliced over this player's staged copy so he is
  // counted once (SE-7002's rule, applied to the server's view of the scenario
  // as well as to the client's delta arithmetic).
  //
  // Rebuilt on every render because `terms` is — the modal constructs it
  // inline. That is deliberate and costs nothing: `useDraftEvaluation`
  // debounces on the serialized VALUE, so a render that changed nothing the
  // user typed issues no request.
  const draftActionsPayload = useMemo(
    () =>
      mergeEditedAction(
        buildDraftPayload(draftActions, draftDetails, draftSignedPlayers),
        playerId ?? "",
        terms
      ),
    [draftActions, draftDetails, draftSignedPlayers, playerId, terms]
  );

  // The club's book value BEFORE this scenario, which the "Net book value"
  // row states a position against (SE-8794). The server cannot supply it — its
  // NET_BOOK_VALUE is per player, never club-scoped — so it used to arrive as
  // null for every club and the row read "Not configured". Summed here; the
  // tier gate is applied by `useDraftEvaluation`, which holds the allow-list.
  const rosterNetBookValue = useMemo(
    () => sumRosterNetBookValue(players),
    [players]
  );

  // The club's REAL verdict (SE-8458).
  //
  // This used to be two constants from `adapters/complianceFixtures.ts` — a
  // club with €220M revenue, a €110M wage bill and a squad already at the
  // registration limit — passed unconditionally for every organization. The
  // deltas the user typed were real; the club they were measured against was
  // invented, and the panel said BREACH or OK with full confidence either way.
  //
  // SE-7583 introduced those constants for a good reason (the modal was
  // reporting "not configured" beside a Compliance tab showing a configured,
  // breaching regime) but fixed the disagreement with a second fixture rather
  // than with the club's own figures. The backend evaluates unsaved scenario
  // actions against the real regime (SE-7410), so both surfaces can now read
  // the same source and the two genuinely agree instead of coincidentally
  // matching.
  //
  // Skipped until the terms are worth evaluating: below that bar the panel
  // renders `awaiting-input` and shows no verdict at all, so a request would
  // buy nothing.
  const { evaluation } = useDraftEvaluation({
    scenarioId: editingScenarioId,
    draftActions: draftActionsPayload,
    skip: !hasSufficientTerms(terms),
    rosterNetBookValue,
  });

  const { data } = useScenarioImpact({
    playerId,
    terms,
    stagedActions,
    playerBaselines,
    serverEvaluation: evaluation,
  });

  return { model: data, scenarioId: editingScenarioId };
};
